import { useCallback, useEffect, useMemo, useState } from 'react';
import { type AuditEntry, Stewardship } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Button, DataTable, EmptyState } from '@beyond-js/ui/react';
import { Panel, Problem, useTableAddress, useUnread } from '@accounts/ui';

/**
 * An organization's activity, for its owners and administrators (ACC-12, ACC-21): who invited,
 * removed, changed or handed over whom, by name, newest first, with older entries on request, as the
 * family's table: sorted, its state in the address. It is a history, read a page at a time, so it has
 * one pager, Show older, and an end line once nothing is older; it offers no search, which could only
 * search the entries read so far and say a false "no match" about the rest.
 *
 * When Accounts does not answer while it is shown, the page's notice says so once (D163): the region says
 * it in its place, over the entries kept from the last read with when that was, and for older entries
 * asked for at the table's end. A hidden view's read joins the notice once it is shown.
 */
export function Audit({ organization, shown }: { organization: string; shown: boolean }) {
	const { text, date, client, language } = usePresence();
	const stewardship = useMemo(() => new Stewardship(client), [client]);
	const [entries, setEntries] = useState<AuditEntry[]>();
	const [next, setNext] = useState<number>();
	// When the newest entries were last read; why they could not be read, and why older ones could not be
	const [at, setAt] = useState<number>();
	const [unread, setUnread] = useState<Refusal>();
	const [older, setOlder] = useState<Refusal>();
	const [busy, setBusy] = useState(false);
	const [state, onState] = useTableAddress('audit');

	// Each resolves whether Accounts answered, which takes the read out of the page's outage
	const read = useCallback(
		() =>
			Refusal.answered(async () => {
				const page = await stewardship.audit(organization);
				setEntries(page.entries);
				setNext(page.next);
				setAt(Date.now());
			}, setUnread),
		[organization, stewardship]
	);
	const more = useCallback(async () => {
		setBusy(true);
		try {
			return await Refusal.answered(async () => {
				const page = await stewardship.audit(organization, next);
				setEntries(current => [...(current ?? []), ...page.entries]);
				setNext(page.next);
			}, setOlder);
		} finally {
			setBusy(false);
		}
	}, [organization, stewardship, next]);
	const outage = useUnread({ key: 'audit', title: text('audit.title'), unread, run: read }, shown);
	const earlier = useUnread({ key: 'audit.older', title: text('outage.older'), unread: older, run: more }, shown);

	useEffect(() => {
		void read();
	}, [read]);

	function describe(entry: AuditEntry): string {
		const values = {
			actor: entry.actor ? (entry.actor.name ?? text('audit.someone')) : text('audit.system'),
			subject: entry.subject?.name ?? text('audit.someone'),
			role: entry.role ? text(`role.${entry.role}`) : '',
			action: entry.action
		};
		const key = `audit.${entry.action}`;
		return text(key) === '—' ? text('audit.other', values) : text(key, values);
	}

	return (
		<Panel id="audit" title={text('audit.title')} unavailable={outage && !entries} updated={outage && entries ? (at ?? null) : undefined}>
			{unread && !outage ? <Problem error={unread} /> : null}
			{older && !earlier ? <Problem error={older} /> : null}
			{/* While the first page loads, the table draws skeleton rows in its own shape (DQ-16) */}
			{entries || !unread ? (
				<DataTable<AuditEntry>
					label={text('audit.title')}
					search={false}
					// Every entry read is shown: Show older is the history's one pager
					size={Math.max(50, entries?.length ?? 0)}
					columns={[
						{ key: 'what', label: text('audit.column.what'), primary: true, value: describe },
						{ key: 'at', label: text('audit.column.when'), sort: (a, b) => Date.parse(a.at) - Date.parse(b.at), value: entry => date(entry.at), align: 'end' }
					]}
					rows={entries ?? []}
					status={entries ? 'ready' : 'loading'}
					rowKey={entry => String(entry.seq)}
					state={state}
					onChange={onState}
					empty={{ title: text('audit.empty') }}
					labels={DataTable.labels[language]}
				/>
			) : null}
			{/* Older entries that did not answer say so where they would be; the page's notice reads them again */}
			{earlier ? (
				<EmptyState kind="outage" frame={false} labels={EmptyState.labels[language]} />
			) : next ? (
				<Button variant="quiet" busy={busy} onClick={() => void more()}>
					{text('audit.more')}
				</Button>
			) : entries?.length ? (
				<p className="text-small text-ink-muted">{text('audit.end')}</p>
			) : null}
		</Panel>
	);
}
